import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, BadgeCheck, ClipboardCheck, Award, FileCheck2, Wrench, Stamp, Users, Star, ChevronDown, MapPin, Clock, FileBadge } from "lucide-react";
import { usePublicConfig } from "@/hooks/usePublicConfig";

/**
 * Trust + compliance summary block — shows DVSA/MOT credentials, service badges
 * and a plain-English compliance checklist that summarises the admin-managed
 * compliance fields. Includes an expandable panel exposing the full address,
 * opening hours and MOT classes pulled live from the admin verification fields.
 */
export function ComplianceChecklist() {
  const { compliance, trust } = usePublicConfig();
  const [expanded, setExpanded] = useState(false);

  const checklistItems: { label: string; detail: string; ok: boolean }[] = [
    {
      label: "DVSA-authorised MOT testing station",
      detail: `VTS ${compliance.vtsNumber} · Classes ${compliance.motClasses}`,
      ok: !!compliance.vtsNumber,
    },
    {
      label: "Registered UK business",
      detail: `${compliance.legalName}${compliance.companyNumber ? ` · Company No. ${compliance.companyNumber}` : ""}`,
      ok: !!compliance.legalName,
    },
    {
      label: "Public liability insurance",
      detail: compliance.insurance,
      ok: !!compliance.insurance,
    },
    {
      label: "12-month workmanship warranty",
      detail: "Parts and labour covered on every repair",
      ok: true,
    },
    {
      label: "Verified workshop address",
      detail: `${compliance.streetAddress}, ${compliance.postalCode} · Yard entrance via 14 Copenhagen Place`,
      ok: !!compliance.postalCode,
    },
    {
      label: "Transparent pricing & written estimates",
      detail: "No work starts without your sign-off",
      ok: true,
    },
  ];

  const serviceBadges = [
    { label: "MOT Class 4", icon: ShieldCheck },
    { label: "MOT Class 5/7", icon: BadgeCheck },
    { label: "Diagnostics", icon: Wrench },
    { label: "Service Stamp", icon: Stamp },
    { label: "All Makes", icon: Users },
  ];

  // Group opening hours into a friendly list (e.g. Mon–Sat 9:00–18:00)
  const openingHoursLines = compliance.openingHours.map((slot) => {
    const days = slot.days.length === 1
      ? slot.days[0]
      : `${slot.days[0].slice(0, 3)}–${slot.days[slot.days.length - 1].slice(0, 3)}`;
    return `${days} · ${slot.opens}–${slot.closes}`;
  });

  return (
    <section className="py-16 md:py-20 bg-secondary/40">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-primary">Authority & compliance</p>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold text-foreground tracking-tight">
            What we're checked, badged and accountable for
          </h2>
          <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
            Independent doesn't mean informal. Every box below is verified — pulled live from our compliance record so you can see the credentials behind the work.
          </p>
        </div>

        {/* Service badges */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-2 md:gap-3">
          {serviceBadges.map(({ label, icon: Icon }) => (
            <span
              key={label}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-bold text-foreground shadow-sm"
            >
              <Icon className="h-3.5 w-3.5 text-primary" />
              {label}
            </span>
          ))}
        </div>

        {/* Checklist + Google rating placeholder grid */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5">
          {/* Compliance checklist */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="rounded-3xl border border-border bg-card p-6 md:p-7 shadow-xl shadow-black/[0.04]"
          >
            <div className="flex items-center gap-2.5 mb-5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <ClipboardCheck className="h-4 w-4" />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-primary">Compliance checklist</p>
                <h3 className="text-base md:text-lg font-bold text-foreground tracking-tight">Verified before we touch your car</h3>
              </div>
            </div>
            <ul className="space-y-3">
              {checklistItems.map((item) => (
                <li key={item.label} className="flex items-start gap-3">
                  <div className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${item.ok ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                    <FileCheck2 className="h-3 w-3" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-foreground leading-tight">{item.label}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">{item.detail}</p>
                  </div>
                </li>
              ))}
            </ul>

            {/* Expandable detail panel — full address, hours, MOT classes */}
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              className="mt-5 inline-flex items-center justify-between gap-2 w-full rounded-xl border border-border bg-secondary/40 px-4 py-2.5 text-xs font-bold text-foreground hover:border-primary/40 hover:text-primary transition-colors"
            >
              <span className="inline-flex items-center gap-2">
                <FileBadge className="h-3.5 w-3.5 text-primary" />
                {expanded ? "Hide" : "See"} verified address, hours & MOT classes
              </span>
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence initial={false}>
              {expanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="rounded-xl border border-border bg-background p-3">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-primary inline-flex items-center gap-1.5">
                        <MapPin className="h-3 w-3" /> Verified address
                      </p>
                      <p className="mt-1.5 text-sm font-bold text-foreground leading-tight">{compliance.streetAddress}</p>
                      <p className="text-xs text-muted-foreground">{compliance.addressLocality}, {compliance.postalCode}</p>
                      <p className="mt-2 text-[11px] text-foreground border-t border-border pt-2">
                        <span className="font-semibold">Yard entrance:</span> 14 Copenhagen Place, E14 7DF
                      </p>
                    </div>
                    <div className="rounded-xl border border-border bg-background p-3">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-primary inline-flex items-center gap-1.5">
                        <Clock className="h-3 w-3" /> Opening hours
                      </p>
                      <ul className="mt-1.5 space-y-0.5 text-xs text-foreground">
                        {openingHoursLines.map((line) => (
                          <li key={line} className="font-semibold">{line}</li>
                        ))}
                      </ul>
                      <p className="mt-2 text-[11px] text-muted-foreground border-t border-border pt-2">
                        Sunday closed
                      </p>
                    </div>
                    <div className="rounded-xl border border-border bg-background p-3">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-primary inline-flex items-center gap-1.5">
                        <ShieldCheck className="h-3 w-3" /> MOT classes
                      </p>
                      <p className="mt-1.5 text-sm font-bold text-foreground leading-tight">VTS {compliance.vtsNumber}</p>
                      <p className="text-xs text-muted-foreground">Classes {compliance.motClasses}</p>
                      <p className="mt-2 text-[11px] text-muted-foreground border-t border-border pt-2">
                        DVSA-authorised testing station
                      </p>
                    </div>
                  </div>
                  {compliance.disclaimers?.length > 0 && (
                    <ul className="mt-3 space-y-1.5">
                      {compliance.disclaimers.slice(0, 3).map((d) => (
                        <li key={d} className="text-[11px] text-muted-foreground leading-relaxed">• {d}</li>
                      ))}
                    </ul>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <p className="mt-5 text-[11px] text-muted-foreground border-t border-border pt-3">
              Last reviewed: <span className="font-semibold text-foreground">{compliance.lastReviewed}</span>
            </p>
          </motion.div>

          {/* Google rating placeholder card */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="rounded-3xl border border-border bg-foreground text-background p-6 md:p-7 shadow-xl flex flex-col"
          >
            <div className="flex items-center gap-2.5 mb-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-primary">
                <Award className="h-4 w-4" />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-primary">Google reputation</p>
                <h3 className="text-base font-bold tracking-tight">Verified customer feedback</h3>
              </div>
            </div>
            <div className="flex items-end gap-3">
              <p className="text-5xl font-extrabold text-primary leading-none">{trust.rating}</p>
              <div className="pb-1">
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className={`h-4 w-4 ${i < Math.round(parseFloat(trust.rating)) ? "fill-[#FBBC04] text-[#FBBC04]" : "fill-[#FBBC04]/30 text-[#FBBC04]/50"}`} />
                  ))}
                </div>
                <p className="mt-0.5 text-[11px] text-background/60 font-semibold">{trust.reviewCount}+ Google reviews</p>
              </div>
            </div>
            <p className="mt-4 text-xs text-background/70 leading-relaxed">
              Score and review count reflect our admin-verified record and are refreshed regularly. Check our Google profile for the latest reviews.
            </p>
            <div className="mt-auto pt-5 grid grid-cols-2 gap-2 text-[11px] text-background/70">
              <div className="rounded-lg border border-background/10 bg-background/[0.04] px-2.5 py-2">
                <p className="font-bold text-background">No upselling</p>
                <p className="text-[10px] mt-0.5">we tell you what's needed</p>
              </div>
              <div className="rounded-lg border border-background/10 bg-background/[0.04] px-2.5 py-2">
                <p className="font-bold text-background">Plain English</p>
                <p className="text-[10px] mt-0.5">no jargon, no scare tactics</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
